import { AnimatePresence, easeIn, easeOut, motion } from 'framer-motion'
import React, { useMemo } from 'react'
import { useEventListener } from 'usehooks-ts'

import useModalStore from '@features/modal/model'
import { ModalKey } from '@features/modal/types'

import { cn } from '@shared/lib/cn'
import ButtonClose from '@shared/ui/button-close'

import styles from './styles.module.scss'



const modalVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3, ease: easeOut } },
  exit: {
    opacity: 0,
    transition: { duration: 0.2, ease: easeIn },
  },
}

const ModalRoot: React.FC = () => {
  const { modals, closeModal, getOpenModalsCount } = useModalStore()

  const closeLastModal = () => {
    const lastKey = Array.from(modals.entries()).splice(-1, 1)[0][0]
    const config = modals.get(lastKey)?.config

    if (config?.onClose) {
      config.onClose()
    }

    closeModal(lastKey)
  }

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeLastModal()
    }
  }

  const HAS_MODALS = useMemo(() => {
    return !!getOpenModalsCount()
  }, [ modals ])

  useEventListener('keydown', handleKeydown)

  return (
    <>
      <div
        className={cn(styles.overlay, {
          [styles._show]: HAS_MODALS,
        })}
        onClick={closeLastModal}
      />
      <AnimatePresence>
        {Array.from(modals.entries())
          .filter(([ , modal ]) => modal?.isOpen)
          .map(([ key, modal ]) => {
            const { content, config } = modal!

            return (
              <motion.div
                className={cn(styles.modal, 'modal', config?.classes?.modal, {
                  [styles['disabled-scroll']]: config?.disabledScroll,
                })}
                key={key}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalVariants}
              >
                <div className={cn(styles.body, modal?.config?.classes?.body)}>
                  <div className={cn(styles.content, config?.classes?.content)}>
                    <div className={styles.top}>
                      {config?.title && (
                        <h3 className={styles.title}>{config?.title}</h3>
                      )}

                      {!config?.hideIconClose && (
                        <ButtonClose
                          className={styles.close}
                          theme="grey"
                          onClick={() => {
                            closeModal(key as ModalKey)

                            if (config?.onClose) {
                              config.onClose()
                            }
                          }}
                        />
                      )}
                    </div>

                    {config?.description && (
                      <p className={styles.description}>
                        {config?.description}
                      </p>
                    )}

                    {content}
                  </div>
                </div>
              </motion.div>
            )
          })}
      </AnimatePresence>
    </>
  )
}

export default ModalRoot
